import config from '../config/env';
import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from 'react';
import { safeStorage } from '../utils/safeStorage';

// Theme Types
export type ThemeMode = 'light' | 'dark' | 'auto';
export type ThemeVariant = 'default' | 'compact' | 'comfortable';

export interface ThemeColors {
  // Primary
  primary: string;
  primaryLight: string;
  primaryDark: string;

  // Backgrounds
  bgPrimary: string;
  bgSecondary: string;
  bgTertiary: string;
  bgElevated: string;
  bgOverlay: string;

  // Text
  textPrimary: string;
  textSecondary: string;
  textTertiary: string;
  textMuted: string;
  textInverse: string;

  // Borders
  borderPrimary: string;
  borderSecondary: string;
  borderFocus: string;
  borderError: string;

  // Interactive
  interactiveHover: string;
  interactiveActive: string;
  interactiveDisabled: string;

  // Semantic
  success: string;
  error: string;
  warning: string;
  info: string;

  // Shadows
  shadowSm: string;
  shadowMd: string;
  shadowLg: string;
  shadowXl: string;
}

export interface ThemeConfig {
  mode: ThemeMode;
  variant: ThemeVariant;
  colors: ThemeColors;
  animations: boolean;
  reducedMotion: boolean;
}

// Light Theme
const lightColors: ThemeColors = {
  primary: '#215EA1',
  primaryLight: '#3a7bc8',
  primaryDark: '#1a4d85',

  bgPrimary: '#ffffff',
  bgSecondary: '#fafbfc',
  bgTertiary: '#f2f4f7',
  bgElevated: '#ffffff',
  bgOverlay: 'rgba(0, 0, 0, 0.4)',

  textPrimary: '#111827',
  textSecondary: '#374151',
  textTertiary: '#6b7280',
  textMuted: '#9ca3af',
  textInverse: '#ffffff',

  borderPrimary: '#e5e7eb',
  borderSecondary: '#d1d5db',
  borderFocus: '#215EA1',
  borderError: '#ef4444',

  interactiveHover: '#f9fafb',
  interactiveActive: '#f3f4f6',
  interactiveDisabled: '#e5e7eb',

  success: '#10b981',
  error: '#ef4444',
  warning: '#f59e0b',
  info: '#3b82f6',

  shadowSm: '0 1px 2px rgba(0, 0, 0, 0.05)',
  shadowMd: '0 4px 6px rgba(0, 0, 0, 0.07)',
  shadowLg: '0 10px 15px rgba(0, 0, 0, 0.1)',
  shadowXl: '0 25px 50px rgba(0, 0, 0, 0.15)',
};

// Dark Theme
const darkColors: ThemeColors = {
  primary: '#3b82f6',
  primaryLight: '#60a5fa',
  primaryDark: '#1e40af',

  bgPrimary: '#1f2937',
  bgSecondary: '#111827',
  bgTertiary: '#374151',
  bgElevated: '#374151',
  bgOverlay: 'rgba(0, 0, 0, 0.8)',

  textPrimary: '#f9fafb',
  textSecondary: '#e5e7eb',
  textTertiary: '#d1d5db',
  textMuted: '#9ca3af',
  textInverse: '#111827',

  borderPrimary: '#374151',
  borderSecondary: '#4b5563',
  borderFocus: '#3b82f6',
  borderError: '#f87171',

  interactiveHover: '#374151',
  interactiveActive: '#4b5563',
  interactiveDisabled: '#374151',

  success: '#34d399',
  error: '#f87171',
  warning: '#fbbf24',
  info: '#60a5fa',

  shadowSm: '0 1px 2px rgba(0, 0, 0, 0.3)',
  shadowMd: '0 4px 6px rgba(0, 0, 0, 0.4)',
  shadowLg: '0 10px 15px rgba(0, 0, 0, 0.5)',
  shadowXl: '0 25px 50px rgba(0, 0, 0, 0.7)',
};

// Default Configurations
const defaultTheme: ThemeConfig = {
  mode: 'auto',
  variant: 'default',
  colors: lightColors,
  animations: true,
  reducedMotion: false,
};

// Theme Context
interface ThemeContextType {
  theme: ThemeConfig;
  isDark: boolean;
  setTheme: (theme: Partial<ThemeConfig>) => void;
  setThemeMode: (mode: ThemeMode) => void;
  setThemeVariant: (variant: ThemeVariant) => void;
  toggleTheme: () => void;
  applyCustomColors: (colors: Partial<ThemeColors>) => void;
  resetTheme: () => void;
  exportTheme: () => string;
  importTheme: (themeJson: string) => boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Compute initial theme+isDark synchronously from localStorage so the FIRST
// render already has the correct CSS variables. Without this, the first
// applyThemeToDom pass writes light defaults on top of the data-theme
// attribute set by the index.html bootstrap, producing a dark→light→dark
// flash. Runs once at provider mount.
const computeInitialState = (): { theme: ThemeConfig; isDark: boolean } => {
  try {
    const saved = safeStorage.getItem('theme-config');
    if (saved) {
      const parsed = JSON.parse(saved);
      const mode: ThemeMode = parsed?.mode || 'light';
      let dark = false;
      if (mode === 'dark') dark = true;
      else if (mode === 'auto' && typeof window !== 'undefined' && window.matchMedia) {
        dark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      }
      return {
        theme: { ...defaultTheme, ...parsed, colors: dark ? darkColors : lightColors },
        isDark: dark,
      };
    }
  } catch { /* malformed / private mode → fall through */ }
  return { theme: defaultTheme, isDark: false };
};

const VALID_MODES: ReadonlySet<ThemeMode> = new Set(['light', 'dark', 'auto']);
const VALID_VARIANTS: ReadonlySet<ThemeVariant> = new Set(['default', 'compact', 'comfortable']);

// CSS color values are short and never contain HTML/CSS-injection characters.
// Reject anything outside this shape so a malformed import can't poison the
// CSS custom properties we apply via setProperty().
const SAFE_COLOR_VALUE = /^[#a-zA-Z0-9.,()\s%-]{1,128}$/;

const REQUIRED_COLOR_KEYS: readonly (keyof ThemeColors)[] = [
  'primary', 'primaryLight', 'primaryDark',
  'bgPrimary', 'bgSecondary', 'bgTertiary', 'bgElevated', 'bgOverlay',
  'textPrimary', 'textSecondary', 'textTertiary', 'textMuted', 'textInverse',
  'borderPrimary', 'borderSecondary', 'borderFocus', 'borderError',
  'interactiveHover', 'interactiveActive', 'interactiveDisabled',
  'success', 'error', 'warning', 'info',
  'shadowSm', 'shadowMd', 'shadowLg', 'shadowXl',
];

function validateImportedTheme(input: unknown): ThemeConfig | null {
  if (!input || typeof input !== 'object') return null;
  const obj = input as Record<string, unknown>;

  if (typeof obj.mode !== 'string' || !VALID_MODES.has(obj.mode as ThemeMode)) return null;
  if (typeof obj.variant !== 'string' || !VALID_VARIANTS.has(obj.variant as ThemeVariant)) return null;

  if (!obj.colors || typeof obj.colors !== 'object') return null;
  const colors = obj.colors as Record<string, unknown>;
  const safeColors = {} as ThemeColors;
  for (const key of REQUIRED_COLOR_KEYS) {
    const value = colors[key];
    if (typeof value !== 'string' || !SAFE_COLOR_VALUE.test(value)) return null;
    safeColors[key] = value;
  }

  return {
    mode: obj.mode as ThemeMode,
    variant: obj.variant as ThemeVariant,
    colors: safeColors,
    animations: typeof obj.animations === 'boolean' ? obj.animations : true,
    reducedMotion: typeof obj.reducedMotion === 'boolean' ? obj.reducedMotion : false,
  };
}

// Theme Provider
export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<ThemeConfig>(() => computeInitialState().theme);
  const [isDark, setIsDark] = useState<boolean>(() => computeInitialState().isDark);

  // Define theme functions first (before useEffects that use them)
  const updateThemeColors = useCallback((dark: boolean) => {
    setTheme(prev => ({
      ...prev,
      colors: dark ? darkColors : lightColors
    }));
  }, []);

  // Update isDark when theme.mode changes (but not for auto mode, which is handled separately)
  useEffect(() => {
    if (theme.mode === 'dark') {
      setIsDark(true);
      updateThemeColors(true);
    } else if (theme.mode === 'light') {
      setIsDark(false);
      updateThemeColors(false);
    }
    // 'auto' mode is handled by the auto-detection effect below
  }, [theme.mode, updateThemeColors]);

  // Handle auto theme detection
  useEffect(() => {
    if (!window.matchMedia) {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    if (!mediaQuery) {
      return;
    }

    const updateTheme = () => {
      if (theme.mode === 'auto' && mediaQuery && typeof mediaQuery.matches !== 'undefined') {
        const shouldBeDark = mediaQuery.matches;
        setIsDark(shouldBeDark);
        updateThemeColors(shouldBeDark);
      }
    };

    updateTheme();
    mediaQuery.addEventListener('change', updateTheme);
    return () => mediaQuery.removeEventListener('change', updateTheme);
  }, [theme.mode, updateThemeColors]);

  // Detect reduced motion preference
  useEffect(() => {
    if (!window.matchMedia) {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!mediaQuery) {
      return;
    }

    setTheme(prev => ({
      ...prev,
      reducedMotion: mediaQuery.matches,
      animations: !mediaQuery.matches
    }));

    const updateMotion = () => {
      setTheme(prev => ({
        ...prev,
        reducedMotion: mediaQuery.matches,
        animations: !mediaQuery.matches
      }));
    };

    mediaQuery.addEventListener('change', updateMotion);
    return () => mediaQuery.removeEventListener('change', updateMotion);
  }, []);

  const applyThemeToDom = useCallback(() => {
    const root = document.documentElement;
    const { colors, animations, variant } = theme;

    // Safety check for colors
    if (!colors) {
      return;
    }

    // Apply CSS custom properties
    Object.entries(colors).forEach(([key, value]) => {
      const cssVar = key.replace(/([A-Z])/g, '-$1').toLowerCase();
      root.style.setProperty(`--theme-${cssVar}`, value);
    });

    // Apply theme attributes
    root.setAttribute('data-theme', isDark ? 'dark' : 'light');
    root.setAttribute('data-theme-variant', variant);
    root.setAttribute('data-animations', animations.toString());
    root.setAttribute('data-reduced-motion', (theme.reducedMotion || false).toString());

    // Apply body classes
    document.body.classList.toggle('dark-theme', isDark);
    document.body.classList.toggle('light-theme', !isDark);
    document.body.classList.toggle('reduced-motion', theme.reducedMotion);
  }, [theme, isDark]);

  useEffect(() => {
    applyThemeToDom();
    safeStorage.setItem('theme-config', JSON.stringify(theme));
  }, [theme, isDark, applyThemeToDom]);

  // Any call from a user-facing control (DarkModeToggle, debug menu, etc.)
  // flips ``THEME_CUSTOMISED_KEY`` to 'true' so the server-default fetch
  // effect below knows to stop overwriting the user's choice on subsequent
  // page loads.
  const THEME_CUSTOMISED_KEY = 'ui-prefs-theme-customised';
  const markThemeCustomised = useCallback(() => {
    try { safeStorage.setItem(THEME_CUSTOMISED_KEY, 'true'); } catch { /* noop */ }
  }, [THEME_CUSTOMISED_KEY]);

  const setThemeMode = useCallback((mode: ThemeMode) => {
    markThemeCustomised();
    setTheme(prev => ({ ...prev, mode }));

    if (mode === 'light') {
      setIsDark(false);
      updateThemeColors(false);
    } else if (mode === 'dark') {
      setIsDark(true);
      updateThemeColors(true);
    }
    // 'auto' mode is handled by the effect hook

    if (config.dev.debug) {
      console.log('[THEME DEBUG] Theme mode changed to:', mode);
    }
  }, [updateThemeColors, markThemeCustomised]);

  const setThemeVariant = useCallback((variant: ThemeVariant) => {
    markThemeCustomised();
    setTheme(prev => ({ ...prev, variant }));
  }, [markThemeCustomised]);

  const toggleTheme = useCallback(() => {
    const newMode = isDark ? 'light' : 'dark';
    setThemeMode(newMode);
  }, [isDark, setThemeMode]);

  const applyCustomColors = useCallback((customColors: Partial<ThemeColors>) => {
    markThemeCustomised();
    setTheme(prev => ({
      ...prev,
      colors: { ...prev.colors, ...customColors }
    }));
  }, [markThemeCustomised]);

  const updateTheme = useCallback((partialTheme: Partial<ThemeConfig>) => {
    markThemeCustomised();
    setTheme(prev => ({ ...prev, ...partialTheme }));
  }, [markThemeCustomised]);

  const resetTheme = useCallback(() => {
    setTheme(defaultTheme);
    safeStorage.removeItem('theme-config');
    safeStorage.removeItem(THEME_CUSTOMISED_KEY);
  }, [THEME_CUSTOMISED_KEY]);

  // Fetch server-side defaults and fill blanks. Skipped when the user has
  // customised their theme locally (``ui-prefs-theme-customised`` = 'true').
  // Runs once on mount and re-runs when AuthContext signals that a fresh
  // token is available.
  useEffect(() => {
    let cancelled = false;
    const applyServerDefaults = async () => {
      if (cancelled) return;
      try {
        if (safeStorage.getItem(THEME_CUSTOMISED_KEY) === 'true') return;
      } catch { /* private mode — treat as not customised */ }
      try {
        const { fetchEffectiveOnce } = await import('../services/uiSettingsService');
        const eff = await fetchEffectiveOnce();
        if (cancelled || !eff) return;
        const s = eff.settings || {};
        setTheme(prev => ({
          ...prev,
          mode: (s.theme_mode as ThemeMode) ?? prev.mode,
          variant: (s.variant as ThemeVariant) ?? prev.variant,
          animations: typeof s.animations === 'boolean' ? s.animations : prev.animations,
        }));
        if (s.theme_mode === 'light') { setIsDark(false); updateThemeColors(false); }
        else if (s.theme_mode === 'dark') { setIsDark(true); updateThemeColors(true); }
      } catch { /* silent — built-ins remain in effect */ }
    };
    applyServerDefaults();
    const listener = () => applyServerDefaults();
    window.addEventListener('app:auth-ready', listener);
    return () => { cancelled = true; window.removeEventListener('app:auth-ready', listener); };
  }, [THEME_CUSTOMISED_KEY, updateThemeColors]);

  const exportTheme = useCallback((): string => {
    return JSON.stringify(theme, null, 2);
  }, [theme]);

  const importTheme = useCallback((themeJson: string): boolean => {
    try {
      const parsed = JSON.parse(themeJson);
      const validated = validateImportedTheme(parsed);
      if (!validated) {
        console.error('[THEME] Imported theme failed validation');
        return false;
      }
      setTheme(validated);
      return true;
    } catch (error) {
      console.error('[THEME] Invalid theme import:', error);
      return false;
    }
  }, []);

  const contextValue: ThemeContextType = useMemo(() => ({
    theme,
    isDark,
    setTheme: updateTheme,
    setThemeMode,
    setThemeVariant,
    toggleTheme,
    applyCustomColors,
    resetTheme,
    exportTheme,
    importTheme,
  }), [theme, isDark, updateTheme, setThemeMode, setThemeVariant, toggleTheme, applyCustomColors, resetTheme, exportTheme, importTheme]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

// Hook
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

